import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Users, LayoutGrid, LifeBuoy, PackageOpen, ShieldCheck, Zap } from "lucide-react";
import { api, LOGO_URL, mediaSrc } from "@/lib/api";
import { ProductGrid, ProductSkeleton, EmptyState, SectionHeader, Countdown } from "@/components/vaulted";
import { CategoryIcon } from "@/components/CategoryIcon";

function PromoHero({ p }) {
  const to = p.link || (p.landing_page ? `/promo/${p.slug}` : "/marketplace");
  return (
    <section className="relative overflow-hidden border-b border-[#27272A] hero-bg noise" data-testid="promo-hero">
      {p.image_url && <img src={mediaSrc(p.image_url)} alt="" className="absolute inset-0 h-full w-full object-cover opacity-30" />}
      <div className="absolute inset-0 bg-gradient-to-r from-[#09090B] via-[#09090B]/80 to-transparent" />
      <div className="container-v relative py-14 md:py-20 rise">
        <span className="inline-flex items-center gap-2 rounded-full border border-violet-500/30 bg-violet-500/10 px-3 py-1 text-[11px] font-medium text-violet-200"><Zap className="h-3.5 w-3.5" />Limited time</span>
        <h1 data-testid="promo-hero-title" className="mt-5 max-w-3xl text-4xl font-semibold leading-[1.05] tracking-tight text-white sm:text-5xl">{p.title}</h1>
        {p.subtitle && <p className="mt-4 max-w-xl text-base text-zinc-300 md:text-lg">{p.subtitle}</p>}
        {p.promo_code && <p className="mt-4 text-sm text-zinc-400">Use code <code data-testid="promo-hero-code" className="rounded border border-violet-500/40 bg-violet-500/10 px-2 py-0.5 font-mono text-violet-200">{p.promo_code}</code></p>}
        {p.show_countdown && p.ends_at && <Countdown to={p.ends_at} className="mt-5" testId="promo-hero-countdown" />}
        <div className="mt-8 flex gap-3"><Link to={to} data-testid="promo-hero-cta" className="v-btn-primary h-11 px-5">{p.cta_label || "Shop now"} <ArrowRight className="h-4 w-4" /></Link>
          <Link to="/marketplace" className="v-btn-ghost h-11 px-5">Browse all accounts</Link></div>
      </div>
    </section>
  );
}

function SalesStrip({ sales }) {
  if (!sales.length) return null;
  return (
    <section className="container-v pt-10" data-testid="sales-strip">
      <div className="grid gap-3 md:grid-cols-2">
        {sales.slice(0, 4).map((p) => (
          <Link key={p.id} to={p.link || (p.landing_page ? `/promo/${p.slug}` : "/marketplace")} data-testid={`sale-card-${p.id}`}
            className="group relative overflow-hidden rounded-xl border border-violet-500/30 bg-[#111116] p-5 hover:border-violet-400 transition-[border-color]">
            {p.image_url && <img src={mediaSrc(p.image_url)} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20 group-hover:opacity-30 transition-opacity" />}
            <div className="relative"><p className="v-eyebrow">Sale</p><p className="mt-1 text-base font-semibold text-white">{p.title}</p>{p.subtitle && <p className="text-sm text-zinc-400">{p.subtitle}</p>}
              <div className="mt-3 flex flex-wrap items-center gap-3">{p.promo_code && <code className="rounded border border-violet-500/40 px-2 py-0.5 font-mono text-xs text-violet-200">{p.promo_code}</code>}
                {p.show_countdown && p.ends_at && <Countdown to={p.ends_at} testId={`sale-countdown-${p.id}`} />}</div></div>
          </Link>
        ))}
      </div>
    </section>
  );
}

function Hero({ hero }) {
  return (
    <section className="relative overflow-hidden border-b border-[#27272A] hero-bg noise" data-testid="hero-banner">
      <div className="absolute inset-0 grid-lines" />
      <div className="container-v relative grid items-center gap-10 py-14 md:py-20 lg:grid-cols-[1.25fr_1fr]">
        <div className="rise">
          <span className="inline-flex items-center gap-2 rounded-full border border-violet-500/30 bg-violet-500/10 px-3 py-1 text-[11px] font-medium text-violet-200">
            <ShieldCheck className="h-3.5 w-3.5" /> Fortnite accounts · Verified sellers · Instant delivery
          </span>
          <h1 data-testid="hero-heading" className="mt-5 text-4xl font-semibold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-[56px]">
            {hero?.heading || "YOUR MARKETPLACE. YOUR GAME."}
          </h1>
          <p data-testid="hero-subheading" className="mt-4 max-w-xl text-base text-zinc-400 md:text-lg">
            {hero?.subheading || "Buy and sell Fortnite accounts from verified sellers. Stacked lockers, OG skins and instant delivery."}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/marketplace" data-testid="hero-explore-button" className="v-btn-primary h-11 px-5">Explore Marketplace <ArrowRight className="h-4 w-4" /></Link>
            <Link to="/become-a-seller" data-testid="hero-seller-button" className="v-btn-ghost h-11 px-5">Become a Seller</Link>
          </div>
        </div>
        <div className="relative hidden lg:block rise" style={{ animationDelay: "120ms" }}>
          <div className="relative ml-auto aspect-square w-[340px] rounded-2xl border border-[#27272A] bg-[#0d0c13] p-6 shadow-[0_0_80px_-20px_rgba(139,92,246,0.45)]">
            <img src={mediaSrc(hero?.image_url) || LOGO_URL} alt="Vaulted" className="h-full w-full rounded-full object-cover" />
            <div className="absolute -left-10 bottom-10 v-card px-3 py-2 text-xs text-zinc-300 shadow-xl">Instant account delivery</div>
            <div className="absolute -right-4 top-8 v-card px-3 py-2 text-xs text-zinc-300 shadow-xl">OG & stacked lockers</div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Categories({ cats }) {
  return (
    <section className="container-v pt-14" data-testid="popular-categories">
      <SectionHeader eyebrow="Browse" title="Popular Categories" action={<Link to="/categories" className="text-sm v-link flex items-center gap-1">All categories <ArrowRight className="h-3.5 w-3.5" /></Link>} />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {cats.slice(0, 6).map((c, i) => (
          <Link key={c.id} to={`/marketplace?category=${c.slug}`} data-testid={`category-card-${c.slug}`}
            className="group v-card p-4 rise hover:border-violet-500/50 hover:bg-[#1b1a21] transition-[border-color,background-color]" style={{ animationDelay: `${i * 50}ms` }}>
            <div className="mb-3 grid h-9 w-9 place-items-center rounded-md border border-[#27272A] bg-[#111116] group-hover:border-violet-500/40 transition-[border-color]">
              <CategoryIcon name={c.icon} className="h-4 w-4 text-violet-400" />
            </div>
            <p className="text-sm font-medium text-white">{c.name}</p>
            <p className="mt-0.5 text-[11px] text-zinc-500">{c.product_count} {c.product_count === 1 ? "product" : "products"}</p>
          </Link>
        ))}
      </div>
    </section>
  );
}

function ProductSection({ title, eyebrow, items, testId, emptyTitle, link }) {
  return (
    <section className="container-v pt-14" data-testid={testId}>
      <SectionHeader eyebrow={eyebrow} title={title} action={<Link to={link} className="text-sm v-link flex items-center gap-1">View all <ArrowRight className="h-3.5 w-3.5" /></Link>} />
      {items === null ? <ProductSkeleton /> : items.length ? <ProductGrid items={items} testId={`${testId}-grid`} /> : (
        <EmptyState icon={PackageOpen} title={emptyTitle} body="New Fortnite account listings from verified sellers will appear here." testId={`${testId}-empty`}
          action={<Link to="/become-a-seller" className="v-btn-ghost v-btn-sm">Start selling on Vaulted</Link>} />
      )}
    </section>
  );
}

const WHY = [
  { icon: Users, title: "Independent Sellers", body: "Accounts listed by verified, independent sellers." },
  { icon: LayoutGrid, title: "Fortnite Marketplace", body: "Filter by skins, V-Bucks, platform and access level in one place." },
  { icon: LifeBuoy, title: "Customer Support", body: "Get assistance through our support system." },
];

export default function Home() {
  const [site, setSite] = useState({});
  const [cats, setCats] = useState([]);
  const [featured, setFeatured] = useState(null);
  const [recent, setRecent] = useState(null);
  const [promos, setPromos] = useState({ hero: null, sales: [] });
  useEffect(() => {
    api.get("/site").then((r) => setSite(r.data)).catch(() => {});
    api.get("/promotions/active").then((r) => setPromos(r.data)).catch(() => {});
    api.get("/categories").then((r) => setCats(r.data)).catch(() => {});
    api.get("/products", { params: { featured: true, limit: 8 } }).then((r) => setFeatured(r.data.items)).catch(() => setFeatured([]));
    api.get("/products", { params: { sort: "newest", limit: 8 } }).then((r) => setRecent(r.data.items)).catch(() => setRecent([]));
  }, []);
  return (
    <div data-testid="home-page">
      {promos.hero ? <PromoHero p={promos.hero} /> : <Hero hero={site.hero} />}
      <SalesStrip sales={promos.sales} />
      <Categories cats={cats} />
      <ProductSection title="Featured Accounts" eyebrow="Hand-picked" items={featured} testId="featured-products" emptyTitle="No featured accounts yet" link="/marketplace?featured=1" />
      <ProductSection title="Recently Added" eyebrow="Fresh drops" items={recent} testId="recent-products" emptyTitle="No listings yet" link="/marketplace?sort=newest" />
      <section className="container-v pt-16" data-testid="why-vaulted">
        <SectionHeader eyebrow="Why Vaulted?" title="Built for Fortnite players" />
        <div className="grid gap-3 md:grid-cols-3">
          {WHY.map((w) => (
            <div key={w.title} className="v-card p-5 hover:border-violet-500/40 transition-[border-color]">
              <w.icon className="h-5 w-5 text-violet-400" />
              <h3 className="mt-4 text-sm font-semibold text-white">{w.title}</h3>
              <p className="mt-1 text-sm text-zinc-400">{w.body}</p>
            </div>
          ))}
        </div>
      </section>
      <section className="container-v pt-14">
        <div className="relative overflow-hidden rounded-xl border border-[#27272A] bg-[#111116] p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-5 justify-between">
          <div className="absolute inset-0 grid-lines opacity-60" />
          <div className="relative">
            <p className="v-eyebrow">Sell on Vaulted</p>
            <h3 className="mt-2 text-lg font-semibold text-white">Sell your Fortnite accounts</h3>
            <p className="mt-1 text-sm text-zinc-400 max-w-lg">Apply to become a verified seller, list your accounts with full details, and get paid from real sales.</p>
          </div>
          <Link to="/become-a-seller" data-testid="home-seller-cta" className="relative v-btn-primary h-11 px-5">Apply now <ArrowRight className="h-4 w-4" /></Link>
        </div>
      </section>
    </div>
  );
}
